import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { FcConfirmDialog, Icon } from "../schedule/ui";
import stackIcon from "../../../assets/stack.png";
import { RefundModal } from "./MemberActionModals";
import { MAIL_ICON, useReceiptActions } from "../../../components/receipts/ReceiptActions";
import {
  apiResult,
  Badge,
  euro,
  fmtDate,
  invoiceFrequency,
  invoiceItem,
  invoiceStatus,
  isFreePurchase,
  paymentItem,
  paymentStatus,
  paymentVia,
  purchaseName,
  purchaseStatus,
} from "./memberUtils";

/** The four tabs of the member page (new design). */

const DOTS = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <circle cx="12" cy="5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" />
  </svg>
);
const I = {
  ticket: <><path d="M3 9a2 2 0 0 0 0 6v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0 0-6V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" /><path d="M13 5v2M13 17v2M13 11v2" /></>,
  cal: <><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  card: <><rect x="2" y="5" width="20" height="14" rx="3" /><path d="M2 10h20M6 15h4" /></>,
  doc: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></>,
  x: <><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6M9 9l6 6" /></>,
  receipt: <><path d="M6 2h12v20l-3-2-3 2-3-2-3 2z" /><path d="M9 7h6M9 11h6" /></>,
  undo: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></>,
  download: <><path d="M12 3v12M7 10l5 5 5-5" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></>,
  send: <><path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" /></>,
  cash: <><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><path d="M6 12h.01M18 12h.01" /></>,
};

export function EmptyState({ icon, title, text, children }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-3xl border border-gray-200 bg-white px-6 py-12 text-center shadow-[0_10px_30px_rgba(72,27,58,0.05)]">
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
        <Icon className="h-7 w-7" d={icon} />
      </span>
      <div className="mt-1 text-[17px] font-black text-gray-900">{title}</div>
      <div className="max-w-md text-sm text-gray-500">{text}</div>
      {children && <div className="mt-3 flex flex-wrap justify-center gap-2">{children}</div>}
    </div>
  );
}

const SkeletonRows = ({ n = 3, h = "h-16" }) => (
  <div className="flex flex-col gap-2" aria-hidden="true">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className={classNames("animate-pulse rounded-2xl bg-[#F3EEF5]", h)} />
    ))}
  </div>
);

const RowMenu = ({ label, items }) => (
  <Menu as="div" className="relative flex justify-end">
    <MenuButton aria-label={label} className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 data-[open]:bg-gray-100">
      {DOTS}
    </MenuButton>
    <MenuItems anchor="bottom end" className="z-[60] mt-1 w-56 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
      {items.map((it) => (
        <MenuItem key={it.label} disabled={it.disabled}>
          <button
            type="button"
            onClick={it.onClick}
            className={classNames(
              "flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold data-[disabled]:opacity-40",
              it.danger ? "text-[#B4232F] data-[focus]:bg-[#FFF5F6]" : "text-gray-900 data-[focus]:bg-[#F7F4F8]"
            )}
          >
            <Icon className={classNames("h-4 w-4", it.danger ? "" : "text-gray-500")} d={it.icon} />
            {it.label}
          </button>
        </MenuItem>
      ))}
    </MenuItems>
  </Menu>
);

/* ------------------------------------------------------------------ Passes & subscriptions */

// Section icons: the ones the old member page used (GiftIcon, stack.png, ArrowPathIcon).
const GiftGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-[22px] w-[22px]" aria-hidden="true">
    <path d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
  </svg>
);
const StackGlyph = () => <img src={stackIcon} alt="" className="h-[22px] w-[22px]" />;
const RenewGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-[22px] w-[22px]" aria-hidden="true">
    <path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);
const PLUS = (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
    <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
  </svg>
);

/** Tinted section button: plum tint, dark plum with white text on hover. */
const AddButton = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-xl bg-[#F3ECF5] px-4 text-sm font-extrabold text-brown-coffee transition-colors hover:bg-brown-coffee hover:text-white"
  >
    {PLUS}
    {children}
  </button>
);

function PurchaseSection({ id, icon, title, subtitle, items, empty, actions, onCancelSub, onRemovePass }) {
  return (
    <section aria-labelledby={id} className="rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3ECF5] text-brown-coffee">{icon}</span>
        <div className="min-w-0 flex-1">
          <h3 id={id} className="text-[17px] font-black text-gray-900">
            {title} <span className="text-[15px] font-bold text-gray-500">({items.length})</span>
          </h3>
          <p className="mt-0.5 text-[13px] text-gray-600">{subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">{actions}</div>
      </div>
      <div className="mt-4 flex flex-col gap-2.5">
        {items.length ? (
          items.map((p) => <PurchaseCard key={p._id} p={p} onCancelSub={onCancelSub} onRemovePass={onRemovePass} />)
        ) : (
          <p className="rounded-2xl border-[1.5px] border-dashed border-[#E3DCE6] bg-[#FCFBFD] px-4 py-3.5 text-sm text-gray-600">{empty}</p>
        )}
      </div>
    </section>
  );
}

/**
 * Passes & subscriptions tab: always three sections, each with its own add buttons (same split as the old page):
 *  Free credits (getMyPurchases listOf=free: free trial + migrated credits), Credit passes (listOf=classpass), Subscriptions.
 */
export function ProductsTab({ purchases, loading, first, onFreeTrial, onMigration, onAddPass, onAddSubscription, onCancelSub, onRemovePass }) {
  if (loading) return <SkeletonRows n={3} h="h-40" />;
  const common = { onCancelSub, onRemovePass };
  return (
    <div className="flex flex-col gap-4">
      <PurchaseSection
        {...common}
        id="sec-free"
        icon={<GiftGlyph />}
        title="Free credits"
        subtitle="Free trial and migrated credits. Nothing is charged."
        items={purchases.free}
        empty={`${first} has no free credits. Add a free trial credit, or move credits over from your old system with Migration credit.`}
        actions={
          <>
            <AddButton onClick={onFreeTrial}>Free trial credit</AddButton>
            <AddButton onClick={onMigration}>Migration credit</AddButton>
          </>
        }
      />
      <PurchaseSection
        {...common}
        id="sec-pass"
        icon={<StackGlyph />}
        title="Credit passes"
        subtitle="Paid passes with a set number of classes, bought online or added by you."
        items={purchases.classpass}
        empty={`${first} has no credit passes yet. Add one with Add pass (they get a payment link), or they can buy one in the FitClub app.`}
        actions={<AddButton onClick={onAddPass}>Add pass</AddButton>}
      />
      <PurchaseSection
        {...common}
        id="sec-sub"
        icon={<RenewGlyph />}
        title="Subscriptions"
        subtitle="Recurring plans, billed automatically every period."
        items={purchases.subscription}
        empty={`${first} has no subscription. Start one with Add subscription; they get a payment link or an invoice.`}
        actions={<AddButton onClick={onAddSubscription}>Add subscription</AddButton>}
      />
    </div>
  );
}

/** One pass / subscription row. Without onCancelSub / onRemovePass (member portal) it is read-only. */
export function PurchaseCard({ p, onCancelSub, onRemovePass }) {
  const d = p.selectedPassDetail || {};
  const st = purchaseStatus(p);
  const isSub = d.productType === "subscription";
  const total = parseInt(d.noOfAllowedSessions, 10) || 0;
  const used = parseInt(p.total_voucher_used, 10) || 0;
  const left = Math.max(0, parseInt(p.total_voucher_unused, 10) || 0);
  const unlimited = isSub && (d.unlimitedCredits === true || String(d.noOfAllowedSessions) === "unlimited");
  const pctUsed = unlimited ? 100 : total ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const sub = [
    isSub ? (unlimited ? "Unlimited credits" : `${total} credits per period`) : d.isMigratedProduct ? "Migrated credits" : null,
    !isFreePurchase(p) ? euro(p.totalStripeAmount || d.price) : null,
    p.paymentMethod === "Cash" ? "paid in cash" : null,
  ]
    .filter(Boolean)
    .join(" · ");
  let dateLabel = "Valid until";
  let date = d.validUntilConsumed ? "Until used" : fmtDate(p.purchaseExpiredDate);
  if (isSub) {
    if (st.cancelling) dateLabel = "Ends";
    else if (d.isCancel) {
      dateLabel = "Ended";
      date = fmtDate(d.cancelDate || p.purchaseExpiredDate);
    } else if (st.active) dateLabel = "Renews";
    else dateLabel = "Ended";
  }
  const canCancelSub = isSub && st.active && !d.isCancel;
  const canRemovePass = !isSub && st.active && p.paymentStatus === "Paid" && parseFloat(d.price) > 0 && !d.isMigratedProduct && !(d.refund && d.refund.isRefunded);
  const menu = canCancelSub && onCancelSub
    ? [{ label: "Cancel subscription", icon: I.x, danger: true, onClick: () => onCancelSub(p) }]
    : canRemovePass && onRemovePass
    ? [{ label: "Remove pass", icon: I.x, danger: true, onClick: () => onRemovePass(p) }]
    : [];
  return (
    <div className={classNames("grid grid-cols-[minmax(0,1fr)_40px] items-center gap-x-4 gap-y-3 rounded-2xl border border-[#F1EEF2] bg-[#FBF9FC] p-3.5 sm:p-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,0.8fr)_40px]", st.active ? "" : "opacity-75")}>
      <div className="col-start-1 row-start-1 min-w-0 lg:col-start-auto lg:row-start-auto">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[15px] font-extrabold text-gray-900">{purchaseName(p)}</span>
          <Badge tone={st.tone}>{st.label}</Badge>
        </div>
        {sub && <div className="mt-1 text-[13px] text-gray-500">{sub}</div>}
      </div>
      <div className="order-last col-span-2 lg:order-none lg:col-span-1">
        <div className="flex justify-between text-[13px] font-bold text-gray-800">
          <span>{unlimited ? "Unlimited" : `Used ${used} of ${total}`}</span>
          {!unlimited && <span className="text-gray-500">{left} left</span>}
        </div>
        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[#EDE5F0]">
          <div className={classNames("h-full rounded-full", st.active ? "bg-indigo-bz" : "bg-[#B9B1BB]")} style={{ width: `${pctUsed}%` }} />
        </div>
      </div>
      <div className="hidden text-[13px] text-gray-700 lg:block">
        <div className="text-[11px] font-extrabold uppercase tracking-wide text-gray-500">{dateLabel}</div>
        <div className="mt-0.5 font-bold">{date || "–"}</div>
      </div>
      <div className="hidden text-[13px] text-gray-700 lg:block">
        <div className="text-[11px] font-extrabold uppercase tracking-wide text-gray-500">Added by</div>
        <div className="mt-0.5 font-bold">{p.productAddedBy === "business" ? "Club" : onCancelSub ? "Member (app)" : "You"}</div>
      </div>
      <div className="col-start-2 row-start-1 flex justify-end lg:col-start-auto lg:row-start-auto">{menu.length ? <RowMenu label={`Options for ${purchaseName(p)}`} items={menu} /> : null}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ Bookings */

const BOOKING_FILTERS = [
  ["upcoming_confirmed", "Upcoming"],
  ["past_confirmed", "Past"],
  ["cancelled", "Cancelled"],
];

const paidVia = (b) => {
  const pv = b.purchase_voucher_detail;
  if (pv) {
    const t = pv.selectedPassDetail?.productType;
    if (t === "subscription") return { name: purchaseName(pv), sub: "Subscription", tone: "red" };
    if (isFreePurchase(pv)) return { name: purchaseName(pv), sub: "Free credit", tone: "blue" };
    return { name: purchaseName(pv), sub: `${b.bookingQty > 1 ? `${b.bookingQty} credits` : "1 credit"}`, tone: "lilac" };
  }
  if (b.paymentMethod === "payAtVenue") return { name: "Pay at venue", sub: b.paymentStatus === "Paid" ? "Paid" : "Not paid yet", tone: "green" };
  const amt = parseFloat(b.totalStripeAmount) || 0;
  if (amt > 0) return { name: `Paid ${euro(amt)}`, sub: b.paymentMode === "Cash" ? "Cash" : "Online payment", tone: "green" };
  return { name: "–", sub: "", tone: "gray" };
};
const TONE_ICON = { red: "bg-[#FFE8EB] text-[#B4232F]", blue: "bg-[#E7F0FF] text-[#1D4ED8]", lilac: "bg-[#F3E9FF] text-[#6A00BA]", green: "bg-[#E8F7EE] text-[#1F6B3A]", gray: "bg-[#F1EEF2] text-gray-500" };

/**
 * Bookings list. Club view: the club's bookings for one member. Member portal: pass businessId (the member's club),
 * memberView (texts in "you") and onCancel(booking) to show "Cancel booking" on upcoming bookings
 * (not within 3 hours of the start, as in the old member page).
 */
export function BookingsTab({ consumerId, first, businessId: businessIdProp, memberView = false, onCancel = null, refreshKey = 0 }) {
  const userReduxState = useSelector(userState);
  const [filter, setFilter] = React.useState("upcoming_confirmed");
  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const reqRef = React.useRef(0);

  const load = React.useCallback(
    async (pageNo) => {
      const businessId = businessIdProp || userReduxState.userData?._id;
      if (!businessId || !consumerId) return;
      const req = ++reqRef.current;
      if (pageNo === 1) setLoading(true);
      try {
        const res = await axiosInstance({
          method: "get",
          url: `${getUrlForBookings()}getMybookings?teacherId=${businessId}&studentId=${consumerId}&page=${pageNo}&limit=20&filterBy=${filter}&listFor=${userReduxState.businessType}`,
        });
        if (req !== reqRef.current) return;
        const [ok, data] = apiResult(res);
        const list = ok && Array.isArray(data?.data) ? data.data : [];
        setTotal(ok ? parseInt(data?.total, 10) || list.length : 0);
        setRows((prev) => (pageNo === 1 ? list : [...prev, ...list]));
        setPage(pageNo);
      } catch (e) {
        if (req === reqRef.current && pageNo === 1) setRows([]);
      } finally {
        if (req === reqRef.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [consumerId, filter, businessIdProp, userReduxState.userData?._id, userReduxState.businessType, refreshKey]
  );

  React.useEffect(() => {
    load(1);
  }, [load]);

  return (
    <section className="rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
      <div className="flex flex-wrap gap-2">
        {BOOKING_FILTERS.map(([k, l]) => (
          <button
            key={k}
            type="button"
            aria-pressed={filter === k}
            onClick={() => setFilter(k)}
            className={classNames("h-9 rounded-full border px-4 text-sm font-extrabold", filter === k ? "border-indigo-bz bg-[#F3E9FF] text-[#6A00BA]" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")}
          >
            {l}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="mt-4"><SkeletonRows n={4} /></div>
      ) : rows.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 border-t border-gray-100 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz"><Icon className="h-6 w-6" d={I.cal} /></span>
          <div className="text-base font-black text-gray-900">{filter === "upcoming_confirmed" ? "No upcoming bookings" : filter === "past_confirmed" ? "No past bookings" : "No cancelled bookings"}</div>
          <div className="text-sm text-gray-500">{filter === "upcoming_confirmed" ? (memberView ? "You haven't booked a class coming up. Book one in the Schedule." : `${first} hasn't booked a class coming up.`) : "Nothing in this list."}</div>
        </div>
      ) : (
        <>
          <div className="mt-4 hidden grid-cols-[64px_minmax(0,1.7fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1.5fr)] gap-4 px-1 pb-1.5 md:grid">
            {["", "Activity", "Booking", "Status", "Paid via"].map((h, i) => (
              <span key={i} className="text-xs font-extrabold uppercase tracking-wide text-gray-500">{h}</span>
            ))}
          </div>
          <ul>
            {rows.map((b) => {
              const start = moment(b.schedule_class_detail?.classDate || b.activityDate);
              const end = b.schedule_class_detail?.classEndDate ? moment(b.schedule_class_detail.classEndDate) : null;
              const cancelled = b.status === "Cancelled";
              const upcoming = start.isAfter(moment());
              const via = paidVia(b);
              const where = b.schedule_class_detail?.location?.addressName || b.class_detail?.location?.addressName || "";
              return (
                <li key={b._id} className="grid grid-cols-[56px_minmax(0,1fr)] items-center gap-x-4 gap-y-2 border-t border-gray-100 px-1 py-3.5 md:grid-cols-[64px_minmax(0,1.7fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1.5fr)]">
                  <div className={classNames("row-span-3 self-start rounded-2xl py-2 text-center md:row-span-1 md:self-center", upcoming && !cancelled ? "bg-[#F3E9FF] text-brown-coffee" : "bg-[#F3EEF5] text-gray-500")}>
                    <div className="text-[11px] font-extrabold uppercase tracking-wide">{start.format("ddd")}</div>
                    <div className="text-xl font-black leading-tight">{start.format("D")}</div>
                    <div className="text-[11px] font-bold uppercase">{start.format("MMM")}</div>
                  </div>
                  <div className="min-w-0">
                    <div className={classNames("truncate text-[15px] font-extrabold", cancelled ? "text-gray-400 line-through" : "text-gray-900")}>{b.className || b.class_detail?.className || "Class"}</div>
                    <div className="mt-0.5 text-[13px] text-gray-500">{[`${start.format("HH:mm")}${end ? ` – ${end.format("HH:mm")}` : ""}`, where].filter(Boolean).join(" · ")}</div>
                  </div>
                  <div>
                    <span className="inline-block rounded-lg bg-[#F3EEF5] px-2 py-0.5 font-mono text-xs font-bold text-gray-700">#{b.bookingId}</span>
                    <span className="mt-1 block text-xs text-gray-500">{cancelled ? `Cancelled ${fmtDate(b.cancellationDate || b.updatedAt)}` : `Booked ${fmtDate(b.bookingDate || b.createdAt)}`}</span>
                  </div>
                  <div>
                    <Badge tone={cancelled ? "red" : "green"}>{cancelled ? "Cancelled" : "Confirmed"}</Badge>
                  </div>
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className={classNames("flex h-8 w-8 shrink-0 items-center justify-center rounded-xl", TONE_ICON[via.tone])}><Icon className="h-4 w-4" d={I.ticket} /></span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-extrabold text-gray-900">{via.name}</span>
                      {via.sub && <span className="block text-xs text-gray-500">{via.sub}</span>}
                    </span>
                  </div>
                  {onCancel && upcoming && !cancelled && (
                    <div className="col-start-2 md:col-span-5 md:col-start-2 md:flex md:justify-end">
                      {start.clone().subtract(3, "hours").isAfter(moment()) ? (
                        <button type="button" onClick={() => onCancel(b)} className="h-9 rounded-xl border border-[#F3C9CF] bg-white px-3.5 text-sm font-extrabold text-[#B4232F] hover:bg-[#FFF5F6]">
                          Cancel booking
                        </button>
                      ) : (
                        <span className="text-xs font-bold text-gray-500">Can't be cancelled within 3 hours of the start</span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {rows.length < total && (
            <div className="flex justify-center border-t border-gray-100 pt-3">
              <button type="button" onClick={() => load(page + 1)} className="rounded-xl px-4 py-2.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">Show more bookings</button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ Transaction history */

export function TransactionsTab({ clubMemberId, consumerId, first, onChanged = () => null }) {
  const receipts = useReceiptActions();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [refunding, setRefunding] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!clubMemberId) return;
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}getMembersAllPayments`, data: { clubMemberId } });
      const [ok, data, msg] = apiResult(res);
      if (ok) setRows(Array.isArray(data) ? data : []);
      else {
        setRows([]);
        if (msg && !/member detail not found/i.test(msg)) setError(msg);
      }
    } catch (e) {
      setError("Couldn't load the transactions.");
    } finally {
      setLoading(false);
    }
  }, [clubMemberId]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading) return <SkeletonRows n={4} />;
  if (error) return <EmptyState icon={I.card} title="Couldn't load transactions" text={error} />;
  if (!rows.length) return <EmptyState icon={I.card} title="No transactions yet" text={`Payments ${first} makes, online or at the studio, show up here with what they bought.`} />;

  return (
    <section className="rounded-3xl border border-gray-200 bg-white px-4 pb-2 pt-1 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:px-5">
      <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_44px] gap-4 px-1 py-3 md:grid">
        {["Date of purchase", "Item", "Paid via", "Amount", ""].map((h, i) => (
          <span key={i} className={classNames("text-xs font-extrabold uppercase tracking-wide text-gray-500", i === 3 ? "text-right" : "")}>{h}</span>
        ))}
      </div>
      <ul>
        {rows.map((r) => {
          const st = paymentStatus(r);
          const items = [
            { label: "View receipt", icon: I.receipt, disabled: !r.receipt_url, onClick: () => window.open(r.receipt_url, "_blank", "noopener") },
            {
              label: "Email receipt to member",
              icon: MAIL_ICON,
              disabled: !["succeeded", "paid"].includes(r.payment_intent?.status || r.status),
              onClick: () => receipts.email(r.type === "invoice" ? { invoiceId: r.id } : { paymentIntentId: r.id }),
            },
            ...(st.refundable ? [{ label: "Refund payment", icon: I.undo, danger: true, onClick: () => setRefunding(r) }] : []),
          ];
          return (
            <li key={r.id} className="grid grid-cols-[minmax(0,1fr)_auto_44px] items-center gap-x-4 gap-y-1 border-t border-gray-100 px-1 py-3.5 first:border-t-0 md:grid-cols-[minmax(0,1fr)_minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,0.8fr)_44px] md:first:border-t">
              <span className="order-2 col-span-3 text-xs text-gray-500 md:order-none md:col-span-1 md:text-sm md:text-gray-700">{fmtDate(r.created)}</span>
              <span className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="text-[15px] font-extrabold text-gray-900">{paymentItem(r)}</span>
                <Badge tone={st.tone}>{st.label}</Badge>
              </span>
              <span className="order-3 col-span-3 text-xs text-gray-500 md:order-none md:col-span-1 md:text-sm md:text-gray-700">{paymentVia(r)}</span>
              <span className="text-right text-[15px] font-black text-gray-900">{euro(r.amount_total)}</span>
              <RowMenu label="Payment options" items={items} />
            </li>
          );
        })}
      </ul>
      {receipts.modal}
      <RefundModal
        open={!!refunding}
        onClose={() => setRefunding(null)}
        payment={refunding}
        item={refunding ? paymentItem(refunding) : ""}
        consumerId={consumerId}
        onDone={() => {
          load();
          onChanged();
        }}
      />
    </section>
  );
}

/* ------------------------------------------------------------------ Invoices */

export function InvoicesTab({ clubMemberId, first, email, onChanged = () => null }) {
  const receipts = useReceiptActions();
  const { addSuccessToast, addErrorToast } = useToast();
  const [rows, setRows] = React.useState([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState("");
  const [confirm, setConfirm] = React.useState(null); // {inv, kind}
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(
    async (after = "") => {
      if (!clubMemberId) return;
      if (after) setLoadingMore(true);
      else setLoading(true);
      setError("");
      try {
        const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}getConnectCustomerStripeInvoices`, data: { clubMemberId, limit: 25, starting_after: after } });
        const [ok, data, msg] = apiResult(res);
        const list = ok && Array.isArray(data?.data) ? data.data : [];
        setRows((prev) => (after ? [...prev, ...list] : list));
        setHasMore(!!data?.has_more);
        if (!ok && msg && !/member detail not found/i.test(msg)) setError(msg);
      } catch (e) {
        setError("Couldn't load the invoices.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [clubMemberId]
  );

  React.useEffect(() => {
    load("");
  }, [load]);

  const act = async () => {
    const { inv, kind } = confirm;
    setBusy(true);
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}${kind === "send" ? "sendStripeInvoiceEmail" : "markAsPayStripeInvoice"}`, data: { purchase_id: inv.metadata?.bzPurchase_id } });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(kind === "send" ? `Invoice sent to ${email || first}` : "Marked as paid · receipt sent");
        setConfirm(null);
        load("");
        onChanged();
      } else addErrorToast(msg || "That didn't work. Please try again.");
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <SkeletonRows n={4} />;
  if (error) return <EmptyState icon={I.doc} title="Couldn't load invoices" text={error} />;
  if (!rows.length) return <EmptyState icon={I.doc} title="No invoices yet" text="Invoices appear for subscriptions billed by invoice and for products you send an invoice for." />;

  return (
    <section className="rounded-3xl border border-gray-200 bg-white px-4 pb-2 pt-1 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:px-5">
      <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1.7fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_44px] gap-4 px-1 py-3 lg:grid">
        {["Invoice", "For", "Created", "Due", "Amount", "Status", ""].map((h, i) => (
          <span key={i} className="text-xs font-extrabold uppercase tracking-wide text-gray-500">{h}</span>
        ))}
      </div>
      <ul>
        {rows.map((inv) => {
          const st = invoiceStatus(inv);
          const unpaid = st.key === "draft" || st.key === "open" || st.key === "overdue";
          const purchaseId = inv.metadata?.bzPurchase_id;
          const pdf = inv.invoice_pdf || inv.hosted_invoice_url;
          const items = [
            { label: "Download invoice", icon: I.download, disabled: !pdf, onClick: () => window.open(pdf, "_blank", "noopener") },
            ...(st.key === "paid" ? [{ label: "Email receipt to member", icon: MAIL_ICON, onClick: () => receipts.email({ invoiceId: inv.id }) }] : []),
            ...(unpaid && purchaseId ? [{ label: st.key === "draft" ? "Send invoice" : "Send again", icon: I.send, onClick: () => setConfirm({ inv, kind: "send" }) }] : []),
            ...(unpaid && purchaseId && !inv.subscription ? [{ label: "Mark as paid (cash)", icon: I.cash, onClick: () => setConfirm({ inv, kind: "paid" }) }] : []),
          ];
          const amount = (st.key === "paid" ? inv.amount_paid ?? inv.total : inv.amount_due ?? inv.total) / 100;
          return (
            <li key={inv.id} className="grid grid-cols-[minmax(0,1fr)_auto_44px] items-center gap-x-4 gap-y-1 border-t border-gray-100 px-1 py-3.5 first:border-t-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.7fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,0.9fr)_44px] lg:first:border-t">
              <span className="hidden font-mono text-[13px] font-bold text-gray-800 lg:block">{inv.number || "Draft"}</span>
              <span className="min-w-0">
                <span className="block truncate text-[15px] font-extrabold text-gray-900">{invoiceItem(inv)}</span>
                <span className="block text-xs text-gray-500">{invoiceFrequency(inv)}<span className="lg:hidden"> · {inv.number || "Draft"} · {fmtDate(moment.unix(inv.created))}</span></span>
              </span>
              <span className="hidden text-sm text-gray-700 lg:block">{fmtDate(moment.unix(inv.created))}</span>
              <span className="hidden text-sm text-gray-700 lg:block">{inv.due_date ? fmtDate(moment.unix(inv.due_date)) : "–"}</span>
              <span className="text-right text-[15px] font-black text-gray-900 lg:text-left">
                {euro(amount)}
                <span className="mt-1 block lg:hidden"><Badge tone={st.tone}>{st.label}</Badge></span>
              </span>
              <span className="hidden lg:block"><Badge tone={st.tone}>{st.label}</Badge></span>
              <RowMenu label={`Options for invoice ${inv.number || ""}`} items={items} />
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <div className="flex justify-center border-t border-gray-100 py-3">
          <button type="button" disabled={loadingMore} onClick={() => load(rows[rows.length - 1]?.id || "")} className="rounded-xl px-4 py-2.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF] disabled:opacity-60">
            {loadingMore ? "Loading…" : "Show more invoices"}
          </button>
        </div>
      )}
      {receipts.modal}
      <FcConfirmDialog
        open={!!confirm}
        busy={busy}
        title={confirm?.kind === "send" ? `Send this invoice to ${first}?` : "Mark this invoice as paid?"}
        message={
          confirm?.kind === "send"
            ? `Stripe emails it to ${email || first} with a link to pay online.`
            : `Use this when ${first} paid at the studio. The invoice is closed as paid in cash and they get a receipt.`
        }
        confirmLabel={confirm?.kind === "send" ? "Send invoice" : "Mark as paid"}
        cancelLabel="Not now"
        onClose={() => setConfirm(null)}
        onConfirm={act}
      />
    </section>
  );
}
